import { Card, CardBody, CardHeader } from "@flick/fep-library";
import { ReactComponent as ForwardIcon } from "@flick/front_end_patterns/src/images/icon_nav_arrow_forward.svg";
import { useQuery } from "@tanstack/react-query";
import { AccountBalanceWrapper } from "components/AccountBalance/AccountBalanceWrapper";
import { ApplicationJourneyTracker } from "components/ApplicationJourneyTracker/ApplicationJourneyTracker";
import { BillSmootherBalance } from "components/BillSmoother/BillSmootherBalance";
import { CurrentBillPeriodSection } from "components/CurrentBillPeriodSection";
import { DailyUsageSection } from "components/DailyUsageSection";
import { withErrorBoundary } from "components/fallback/withErrorBoundary";
import { LoadingSection } from "components/LoadingSection";
import { OffpeakDetailsSection } from "components/OffpeakDetailsSection";
import { OutstandingBillsList } from "components/OutstandingBills";
import { RecentBills } from "components/RecentBills";
import { NoAvailableData } from "components/Usage/NoAvailableData";
import { WholesalePriceForecastSection } from "components/WholesalePriceForecastSection";
import { addSeconds, differenceInDays } from "date-fns";
import {
  getBillingBalance,
  getLatestReceivableInvoice,
  getOutstandingInvoices,
} from "queries/billing";
import { getBillSmootherConfig } from "queries/payment_smoother";
import { getAggregates } from "queries/rating";
import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { setBillSmootherConfig } from "reducers/billSmoother";
import { useAppDispatch, useAppSelector } from "reduxUtils/hook";
import {
  calculateCurrentPeriodDates,
  calculatePreviousPeriodDates,
  dateUsageDataAvailableFrom,
  previousDataAvailable,
} from "utils/aggregateCalculations/calculatePeriodDates";
import { AVAILABLE_PERIODS, PRODUCT_NAMES } from "utils/constants";
import { jwtClient } from "utils/jwtClient";
import { prepareDailyUsageData } from "utils/prepareDailyUsageData";

import content from "./static/content.json";
const { applicationJourneyWelcome } = content;

interface BillingPeriod {
  code: string;
  duration: number;
  periodName: string;
}

interface PrerenderDetails {
  currentPeriodEndDate: Date;
  currentPeriodStartDate: Date;
  previousPeriodStartDate: Date;
  hasPreviousData: boolean;
  startAt: Date;
}

interface DailyUsageSectionWrapperProps {
  billingPeriod: BillingPeriod;
  billingEntityData: BillingEntity;
  supplyNodeRef: string;
  currentProductName: string;
}

export const getBillingPeriod = (
  billingEntityData?: BillingEntity,
): BillingPeriod => {
  if (!billingEntityData) return undefined;
  return AVAILABLE_PERIODS[
    billingEntityData.nominated_billing_period
  ] as BillingPeriod;
};

export const productIsOffpeak = (currentProduct?: CurrentProduct): boolean =>
  currentProduct && currentProduct.normalisedName
    ? currentProduct.normalisedName === PRODUCT_NAMES.offPeak
    : false;

export const productIsWholesale = (currentProduct?: CurrentProduct): boolean =>
  currentProduct && currentProduct.normalisedName
    ? currentProduct.normalisedName === PRODUCT_NAMES.wholesale
    : false;

/**
 * @description preparation values for queryFetch and render DailyUsageSection
 */
const getPrerenderDetails = (
  billingPeriod: BillingPeriod,
  billingEntityData: BillingEntity,
): PrerenderDetails => {
  const { endDate: currentPeriodEndDate, startDate: currentPeriodStartDate } =
    calculateCurrentPeriodDates(+billingPeriod.duration);

  const { startDate: previousPeriodStartDate } = calculatePreviousPeriodDates(
    currentPeriodStartDate,
    +billingPeriod.duration,
  );
  const hasPreviousData = previousDataAvailable(
    +billingPeriod.duration,
    new Date(billingEntityData.start_at),
  );

  const startAt =
    hasPreviousData && previousPeriodStartDate
      ? previousPeriodStartDate
      : currentPeriodStartDate;

  return {
    currentPeriodEndDate,
    currentPeriodStartDate,
    previousPeriodStartDate,
    hasPreviousData,
    startAt,
  };
};

/**
 * @description calculate daily usage, default is empty array
 */
const getDailyUsageData = (
  aggregatesData: Aggregates,
  prepDetails: PrerenderDetails,
): DailyUsage[] => {
  if (!aggregatesData || !prepDetails) return [];
  const timePeriodDifference = differenceInDays(
    addSeconds(new Date(prepDetails.currentPeriodEndDate), 1),
    new Date(prepDetails.currentPeriodStartDate),
  );

  return prepareDailyUsageData(
    aggregatesData,
    prepDetails.currentPeriodStartDate,
    timePeriodDifference,
  );
};

/**
 * @description renders the Application Journey Tracker when it's called from the HomePage
 */
const ApplicationJourneyWrapper = (): JSX.Element => {
  return (
    <>
      <h1 className="heading heading_page">{applicationJourneyWelcome}</h1>
      <ApplicationJourneyTracker />
    </>
  );
};

/**
 * @description renders the DailyUsageSection when it's called from the HomePage
 */
const DailyUsageSectionWrapper =
  withErrorBoundary<DailyUsageSectionWrapperProps>(
    ({
      billingPeriod,
      billingEntityData,
      supplyNodeRef,
      currentProductName,
    }: DailyUsageSectionWrapperProps) => {
      const prepDailyUsageDetails = useMemo(
        () => getPrerenderDetails(billingPeriod, billingEntityData),
        [billingPeriod, billingPeriod],
      );

      const { data: aggregatesData, isLoading: isAggregateLoading } = useQuery<
        Aggregates,
        Error
      >(
        [
          "aggregates",
          {
            supplyNodeRef,
            startAt: prepDailyUsageDetails.startAt,
            endAt: prepDailyUsageDetails.currentPeriodEndDate,
            jwtClient,
          },
        ],
        getAggregates,
        {
          enabled: Boolean(billingEntityData) && Boolean(supplyNodeRef),
          useErrorBoundary: true,
        },
      );

      if (isAggregateLoading) return <LoadingSection />;

      const dailyUsageData = getDailyUsageData(
        aggregatesData,
        prepDailyUsageDetails,
      );

      // if dailyUsageData is empty array then fallbacks
      if (dailyUsageData.length === 0) {
        return (
          <NoAvailableData
            startDate={dateUsageDataAvailableFrom(
              new Date(billingEntityData.start_at),
            )}
          />
        );
      }

      return (
        <DailyUsageSection
          sectionSubHeading={`For the last ${billingPeriod.periodName}`}
          previousPeriodStartDate={
            prepDailyUsageDetails.previousPeriodStartDate
          }
          hasPreviousData={prepDailyUsageDetails.hasPreviousData}
          productName={currentProductName}
          dailyUsageData={dailyUsageData}
          periodName={billingPeriod.periodName}
        />
      );
    },
  );

/**
 * @description HomePage
 */
export const HomePage = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { active, supplyNodeRef, accountNumber, customerNumber } =
    useAppSelector((store) => store.currentAccount);
  const { billingEntityData } = useAppSelector(
    (store) => store.billingEntityData,
  );
  const currentProduct = useAppSelector((store) => store.currentProduct);
  const { billSmootherConfig: billSmootherState } = useAppSelector(
    (store) => store.billSmootherConfig,
  );

  const billingPeriod: BillingPeriod = useMemo(
    () => getBillingPeriod(billingEntityData),
    [billingEntityData],
  );

  const { data: billSmootherConfig } = useQuery(
    ["billSmootherConfig"],
    () => getBillSmootherConfig(billingEntityData?.id, jwtClient),
    { enabled: Boolean(billingEntityData) },
  );

  const {
    data: accountBalance,
    isFetching: isAccountBalanceFetching,
    isError: isAccountBalanceError,
  } = useQuery(
    ["accountBalance", { customerNumber, jwtClient }],
    getBillingBalance,
    {
      enabled: Boolean(customerNumber),
    },
  );

  const {
    data: outstandingInvoices,
    isError: isOutstandingInvoicesError,
    isFetching: isOutstandingInvoicesFetching,
  } = useQuery(
    ["outstandingInvoices", { customerNumber, jwtClient }],
    getOutstandingInvoices,
    {
      enabled: Boolean(customerNumber),
    },
  );

  const {
    data: latestReceivableInvoice,
    isError: isLatestReceivableInvoiceError,
    isFetching: isLatestReceivableInvoiceFetching,
  } = useQuery(
    ["latestReceivableInvoice", { customerNumber, jwtClient }],
    getLatestReceivableInvoice,
    {
      enabled: Boolean(customerNumber),
    },
  );

  useEffect(() => {
    if (!billSmootherState && billSmootherConfig) {
      dispatch(setBillSmootherConfig({ billSmootherConfig }));
    }
  }, [billSmootherConfig]);

  if (!Boolean(accountNumber)) {
    return <ApplicationJourneyWrapper />;
  }

  return (
    <div className="page_section">
      <section className="page_section__column page_section__column--1">
        {productIsOffpeak(currentProduct) && active && (
          <>
            <Card cardBorderStyle="shadow" cardPaddingType="compact">
              <CardHeader headingColor="black">
                <h2 className="h4">Off Peak details</h2>
              </CardHeader>
              <CardBody>
                <OffpeakDetailsSection />
              </CardBody>
            </Card>
            <div className="spacer--large" />
          </>
        )}
        {productIsWholesale(currentProduct) && active && (
          <>
            <Card cardBorderStyle="shadow" cardPaddingType="compact">
              <CardHeader headingColor="black">
                <h2 className="h4">Wholesale details</h2>
              </CardHeader>
              <CardBody>
                <WholesalePriceForecastSection />
              </CardBody>
            </Card>
            <div className="spacer--large" />
          </>
        )}
        {Boolean(customerNumber) && (
          <>
            <Card cardBorderStyle="shadow" cardPaddingType="compact">
              <CardHeader headingColor="black">
                <h2 className="h4">Your account balance</h2>
              </CardHeader>
              <CardBody>
                <>
                  <AccountBalanceWrapper
                    accountBalance={accountBalance}
                    isAccountBalanceError={isAccountBalanceError}
                    isAccountBalanceFetching={isAccountBalanceFetching}
                    isEnrolledInBillSmoother={billSmootherConfig?.enrolled}
                  />

                  <OutstandingBillsList
                    outstandingInvoices={outstandingInvoices}
                    isOutstandingInvoicesError={isOutstandingInvoicesError}
                    isOutstandingInvoicesFetching={
                      isOutstandingInvoicesFetching
                    }
                    latestReceivableInvoice={latestReceivableInvoice}
                    isLatestReceivableInvoiceError={
                      isLatestReceivableInvoiceError
                    }
                    isLatestReceivableInvoiceFetching={
                      isLatestReceivableInvoiceFetching
                    }
                  />
                </>
              </CardBody>
            </Card>
            <div className="spacer--large" />
          </>
        )}
        {billSmootherState?.enrolled && Boolean(customerNumber) && (
          <>
            <Card cardBorderStyle="shadow" cardPaddingType="compact">
              <CardHeader headingColor="black">
                <h2 className="h4">Your Bill Smoother balance</h2>
              </CardHeader>
              <CardBody>
                <>
                  <BillSmootherBalance
                    accountBalance={accountBalance}
                    isAccountBalanceError={isAccountBalanceError}
                    isAccountBalanceFetching={isAccountBalanceFetching}
                  />
                  <article className="customer_tools_section__link_container">
                    <Link
                      to="/bills/bill-smoother"
                      className="customer_tools_section__link"
                      state={{ previousPath: window.location.pathname }}
                    >
                      View Bill Smoother details
                    </Link>
                    <ForwardIcon />
                  </article>
                </>
              </CardBody>
            </Card>
            <div className="spacer--large" />
          </>
        )}
        <Card cardBorderStyle="shadow" cardPaddingType="compact">
          <CardHeader headingColor="black">
            <h2 className="h4">Your bill</h2>
          </CardHeader>
          <CardBody>
            <>
              {billingEntityData && active && (
                <CurrentBillPeriodSection
                  componentTitle={"Current bill period"}
                />
              )}
              <RecentBills />
            </>
          </CardBody>
        </Card>
        <div className="spacer--large" />
      </section>
      <section className="page_section__column page_section__column--2">
        <Card cardBorderStyle="shadow" cardPaddingType="compact">
          <CardHeader headingColor="black">
            <h2 className="h4">Your usage</h2>
          </CardHeader>
          <CardBody>
            <>
              {billingPeriod && active && currentProduct && supplyNodeRef && (
                <DailyUsageSectionWrapper
                  billingPeriod={billingPeriod}
                  billingEntityData={billingEntityData}
                  supplyNodeRef={supplyNodeRef}
                  currentProductName={currentProduct.normalisedName}
                  componentTitle="Daily usage"
                />
              )}
            </>
          </CardBody>
        </Card>
      </section>
    </div>
  );
};
